@let current = view();
<h2 class="activation__title" id="activation-dialog-title" mat-dialog-title>
  {{ 'TenantLifecycle.Checkout.Title' | translate }}
</h2>

<mat-dialog-content class="activation" [attr.data-view]="current" data-testid="activation-dialog">
  @switch (current) {
    @case ('loading') {
      <div class="activation__status" role="status">
        <mat-spinner aria-hidden="true" diameter="32" />
        <p>{{ 'TenantLifecycle.Checkout.Loading' | translate }}</p>
      </div>
    }
    @case ('processing') {
      <div class="activation__status" role="status">
        <mat-spinner aria-hidden="true" diameter="32" />
        <p>{{ 'TenantLifecycle.Checkout.Processing' | translate }}</p>
      </div>
    }
    @case ('handoff') {
      <p class="activation__text" data-testid="activation-handoff">
        {{
          (handoff() === 'contact' ? 'TenantLifecycle.Checkout.HandoffContact' : 'TenantLifecycle.Checkout.HandoffLink')
            | translate
        }}
      </p>
    }
    @case ('saved') {
      <div class="activation__summary" data-testid="activation-summary">
        <span class="activation__plan-name">
          {{
            (selection().rss ? 'TenantLifecycle.Checkout.PlanWithRss' : 'TenantLifecycle.Checkout.PlanOnly')
              | translate: { plan: planName(selection().plan) }
          }}
        </span>
        @if (price(); as priceText) {
          <span class="activation__summary-price">&ngsp;{{ priceText.key | translate: priceText.params }}</span>
        }
      </div>

      @if (savedMethod(); as method) {
        <div class="activation__card" data-testid="activation-saved-card">
          <mat-icon aria-hidden="true">{{ savedMethodIcon() }}</mat-icon>
          <span>{{ method.key | translate: method.params }}</span>
        </div>
      }
      <div class="activation__links">
        <button
          (click)="useOtherCard()"
          [disabled]="busy()"
          class="activation__link"
          data-testid="activation-use-other-card"
          type="button"
        >
          {{ 'TenantLifecycle.Checkout.UseOtherMethod' | translate }}
        </button>
        <button
          (click)="toPlan()"
          [disabled]="busy()"
          class="activation__link"
          data-testid="activation-change-plan"
          type="button"
        >
          {{ 'TenantLifecycle.Checkout.ChangePlan' | translate }}
        </button>
      </div>
    }
    @case ('plan') {
      <p class="activation__step">{{ 'TenantLifecycle.Checkout.StepPlan' | translate }}</p>

      <fieldset class="activation__plans">
        <legend class="activation__legend">{{ 'TenantLifecycle.Checkout.PlanLegend' | translate }}</legend>
        @for (plan of plans; track plan) {
          @let planPriceText = planPrice(plan);
          <label
            class="activation__plan"
            [class.activation__plan--selected]="selection().plan === plan"
            [class.activation__plan--disabled]="!availability().plans[plan]"
            [attr.data-testid]="'activation-plan-' + plan"
          >
            <input
              (change)="selectPlan(plan)"
              [checked]="selection().plan === plan"
              [disabled]="!availability().plans[plan]"
              class="activation__plan-radio"
              name="activation-plan"
              type="radio"
            />
            <span class="activation__plan-name">{{ planName(plan) }}</span>
            <span class="activation__plan-price">
              @if (planPriceText) {
                {{ planPriceText.key | translate: planPriceText.params }}
              } @else {
                {{ 'TenantLifecycle.Checkout.NotAvailable' | translate }}
              }
            </span>
          </label>
        }
      </fieldset>

      <div class="activation__row">
        <span class="activation__legend" id="activation-interval-label">
          {{ 'TenantLifecycle.Checkout.IntervalLegend' | translate }}
        </span>
        <mat-button-toggle-group
          (change)="selectInterval($event.value)"
          [value]="selection().interval"
          aria-labelledby="activation-interval-label"
          class="activation__interval"
          hideSingleSelectionIndicator
        >
          @for (interval of intervals; track interval) {
            <mat-button-toggle
              [attr.data-testid]="'activation-interval-' + interval"
              [disabled]="!availability().intervals[interval]"
              [value]="interval"
            >
              {{
                (interval === 'year' ? 'TenantLifecycle.Checkout.Yearly' : 'TenantLifecycle.Checkout.Monthly')
                  | translate
              }}
            </mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>

      <mat-checkbox
        (change)="setRss($event.checked)"
        [checked]="selection().rss"
        [disabled]="!availability().rssToggle"
        class="activation__rss"
        data-testid="activation-rss"
      >
        {{ 'TenantLifecycle.Checkout.Rss' | translate }}
      </mat-checkbox>
    }
    @case ('payment') {
      <p class="activation__step">{{ 'TenantLifecycle.Checkout.StepPayment' | translate }}</p>

      <div class="activation__summary" data-testid="activation-summary">
        <span class="activation__plan-name">
          {{
            (selection().rss ? 'TenantLifecycle.Checkout.PlanWithRss' : 'TenantLifecycle.Checkout.PlanOnly')
              | translate: { plan: planName(selection().plan) }
          }}
        </span>
        @if (price(); as priceText) {
          <span class="activation__summary-price">&ngsp;{{ priceText.key | translate: priceText.params }}</span>
        }
      </div>

      @if (!stripeReady() && !errorKey()) {
        <div class="activation__status activation__status--inline" role="status">
          <mat-spinner aria-hidden="true" diameter="24" />
          <span>{{ 'TenantLifecycle.Checkout.PaymentLoading' | translate }}</span>
        </div>
      }
      <div
        class="activation__payment-element"
        data-testid="activation-payment-element"
        hypecastAdminPaymentElementHost
      ></div>
      @if (paymentError(); as message) {
        <p class="activation__error" data-testid="activation-payment-error" role="alert">{{ message }}</p>
      }
      @if (savedMethod()) {
        <button
          (click)="useSavedCard()"
          [disabled]="busy()"
          class="activation__link"
          data-testid="activation-use-saved-card"
          type="button"
        >
          {{ 'TenantLifecycle.Checkout.UseSavedMethod' | translate }}
        </button>
      }
    }
    @case ('payment-failed') {
      <div class="activation__result activation__result--error" data-testid="activation-result" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <div>
          <p class="activation__result-title">{{ 'TenantLifecycle.Checkout.PaymentFailed' | translate }}</p>
          <p class="activation__text">{{ 'TenantLifecycle.Checkout.PaymentFailedHint' | translate }}</p>
        </div>
      </div>
    }
    @case ('blocked') {
      <div class="activation__result activation__result--error" data-testid="activation-result" role="alert">
        <mat-icon aria-hidden="true">block</mat-icon>
        @if (errorKey(); as key) {
          <p class="activation__text">{{ key | translate }}</p>
        }
      </div>
    }
    @default {
      @let success = successText();
      <div class="activation__result" data-testid="activation-result" role="status">
        <mat-icon aria-hidden="true">check_circle</mat-icon>
        <p class="activation__text">{{ success.key | translate: success.params }}</p>
      </div>
    }
  }

  @if (current !== 'blocked') {
    @if (errorKey(); as key) {
      <p class="activation__error" data-testid="activation-error" role="alert">{{ key | translate }}</p>
    }
  }
</mat-dialog-content>

<mat-dialog-actions class="activation__actions">
  @switch (current) {
    @case ('saved') {
      @let savedDisabled = busy() || option() === null;
      <div class="activation__submit">
        <div class="activation__buttons">
          <button
            (click)="close()"
            [class.hypecast-secondary-button--disabled]="busy()"
            [disabled]="busy()"
            class="hypecast-secondary-button"
            type="button"
          >
            {{ 'TenantLifecycle.Checkout.Cancel' | translate }}
          </button>
          <button
            (click)="activate()"
            [attr.aria-busy]="busy()"
            [class.disabled]="savedDisabled"
            [disabled]="savedDisabled"
            class="hypecast-primary-button"
            data-testid="activation-submit"
            type="button"
          >
            {{ (busy() ? 'TenantLifecycle.Checkout.Submitting' : 'TenantLifecycle.Activate') | translate }}
          </button>
        </div>
        @let billed = billedFrom();
        <p class="activation__charge" data-testid="activation-billed-from">
          {{ billed.key | translate: billed.params }}
        </p>
      </div>
    }
    @case ('plan') {
      <button (click)="close()" class="hypecast-secondary-button" type="button">
        {{ 'TenantLifecycle.Checkout.Cancel' | translate }}
      </button>
      <button
        (click)="toPayment()"
        [class.disabled]="option() === null"
        [disabled]="option() === null"
        class="hypecast-primary-button"
        data-testid="activation-next"
        type="button"
      >
        {{ 'TenantLifecycle.Checkout.Continue' | translate }}
      </button>
    }
    @case ('payment') {
      @let disabled = busy() || option() === null || !stripeReady();
      <div class="activation__submit">
        <div class="activation__buttons">
          <button
            (click)="toPlan()"
            [class.hypecast-secondary-button--disabled]="busy()"
            [disabled]="busy()"
            class="hypecast-secondary-button"
            type="button"
          >
            {{ 'TenantLifecycle.Checkout.Back' | translate }}
          </button>
          <button
            (click)="activate()"
            [attr.aria-busy]="busy()"
            [class.disabled]="disabled"
            [disabled]="disabled"
            class="hypecast-primary-button"
            data-testid="activation-submit"
            type="button"
          >
            {{ (busy() ? 'TenantLifecycle.Checkout.Submitting' : 'TenantLifecycle.Activate') | translate }}
          </button>
        </div>
        @if (chargeLine(); as charge) {
          <p class="activation__charge" data-testid="activation-charge-line">
            {{ charge.key | translate: charge.params }}
          </p>
        }
      </div>
    }
    @case ('handoff') {
      <button (click)="close()" class="hypecast-secondary-button" type="button">
        {{ 'TenantLifecycle.Checkout.Cancel' | translate }}
      </button>
      <button
        (click)="continueHandoff()"
        class="hypecast-primary-button"
        data-testid="activation-handoff-continue"
        type="button"
      >
        {{ (handoff() === 'contact' ? 'TenantLifecycle.Contact' : 'TenantLifecycle.Activate') | translate }}
      </button>
    }
    @case ('payment-failed') {
      @if (result()?.invoiceUrl) {
        <button
          (click)="openInvoice()"
          class="hypecast-secondary-button"
          data-testid="activation-open-invoice"
          type="button"
        >
          {{ 'TenantLifecycle.Checkout.OpenInvoice' | translate }}
        </button>
      }
      <button
        (click)="useOtherCard()"
        class="hypecast-primary-button"
        data-testid="activation-retry-card"
        type="button"
      >
        {{ 'TenantLifecycle.Checkout.UseOtherMethod' | translate }}
      </button>
    }
    @case ('scheduled') {
      <button (click)="close()" class="hypecast-primary-button" data-testid="activation-done" type="button">
        {{ 'TenantLifecycle.Checkout.Done' | translate }}
      </button>
    }
    @case ('activated') {
      <button (click)="close()" class="hypecast-primary-button" data-testid="activation-done" type="button">
        {{ 'TenantLifecycle.Checkout.Done' | translate }}
      </button>
    }
    @case ('processing') {}
    @default {
      <button (click)="close()" class="hypecast-secondary-button" type="button">
        {{ 'TenantLifecycle.Checkout.Close' | translate }}
      </button>
    }
  }
</mat-dialog-actions>
